import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { API, api, errText } from "@/lib/api";

async function streamAsk(text, onDelta) {
  const send = () => fetch(`${API}/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
  let res = await send();
  if (res.status === 401) { await api.post("/auth/refresh"); res = await send(); }
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Ошибка AI-помощника");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      const line = ev.replace(/^data: /, "");
      if (line === "[DONE]") return;
      const d = JSON.parse(line);
      if (d.error) throw new Error(d.error);
      onDelta(d.delta);
    }
  }
}

function Upsell() {
  const { openUpgrade } = useAuth();
  return (
    <div className="rounded-3xl bg-[#0b2e20] text-white p-8 sm:p-12 grain" data-testid="ai-upsell">
      <Sparkles className="h-10 w-10 text-amber-400" />
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-4">AI-помощник доступен в Basic и Pro</h2>
      <p className="text-white/70 mt-2 max-w-md">Объяснит любую тему, разберёт задачу по шагам и поможет подготовиться к СОР и СОЧ.</p>
      <Button onClick={() => openUpgrade("AI-помощник доступен в платной подписке Basic или Pro")} className="mt-6 rounded-full bg-amber-400 text-[#0b2e20] hover:bg-amber-300 font-bold" data-testid="ai-upgrade-button">Купить подписку</Button>
    </div>
  );
}

export default function AiPage() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const pro = user.plan !== "free";

  const load = useCallback(() => { api.get("/ai/history").then((r) => setMsgs(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (pro) load(); }, [pro, load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText(""); setBusy(true);
    setMsgs((p) => [...p, { id: `u${Date.now()}`, role: "user", text: q }, { id: "pending", role: "assistant", text: "" }]);
    try {
      await streamAsk(q, (d) => setMsgs((p) => p.map((m) => (m.id === "pending" ? { ...m, text: m.text + d } : m))));
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); setMsgs((p) => p.map((m) => (m.id === "pending" ? { ...m, id: `a${Date.now()}` } : m))); }
  };
  const clear = async () => {
    try { await api.delete("/ai/history"); setMsgs([]); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between gap-3 rise">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#1f5f42] text-[#a3e6be] grid place-items-center"><Bot className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-2xl sm:text-4xl font-extrabold text-[#0b2e20]">AI-помощник</h1>
            <p className="text-sm text-[#4b6b5c]">Задай вопрос по любому предмету</p>
          </div>
        </div>
        {pro && msgs.length > 0 && <Button variant="outline" size="sm" onClick={clear} className="rounded-full" data-testid="ai-clear-button"><Trash2 className="h-4 w-4 mr-1" />Очистить</Button>}
      </div>
      <div className="mt-5 sm:mt-8">
        {!pro ? <Upsell /> : (
          <div className="rounded-2xl border border-[#d1e7dd] bg-[#eef6f1] overflow-hidden">
            <div className="h-[calc(100dvh-360px)] min-h-[300px] lg:h-[55vh] overflow-y-auto p-3 sm:p-6 space-y-3 scrollbar-thin" data-testid="ai-messages-list">
              {msgs.length === 0 && <p className="text-center text-sm text-[#6b8a7b] py-16">Например: «Объясни теорему Пифагора на примере» или «Составь 5 вопросов к СОЧ по химии, 8 класс»</p>}
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${m.role}`}>
                  <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-[#1f5f42] text-white" : "bg-white border border-[#d1e7dd] text-[#0b2e20]"}`}>
                    {m.text || <span className="text-[#6b8a7b]">Думаю...</span>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form onSubmit={ask} className="flex gap-2 p-3 bg-white border-t border-[#d1e7dd]">
              <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) ask(e); }} maxLength={4000} placeholder="Ваш вопрос..." className="resize-none min-h-[44px]" data-testid="ai-input" />
              <Button type="submit" disabled={busy} className="h-11 rounded-full bg-[#1f5f42] hover:bg-[#174731] px-5" data-testid="ai-send-button"><Send className="h-4 w-4" /></Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
